import { Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import ServicePageLayout from "@/components/ServicePageLayout";
import wechatQr from "@/assets/image/pdf/wechat_224.webp";

interface ContactCard {
  label: string;
  value: string;
}

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.resolvedLanguage?.startsWith("zh");

  const cards = (t("contact.cards", { returnObjects: true }) as ContactCard[])
    .slice()
    .sort((a, b) => {
      const rank = (label: string) => {
        if (/phone|电话/i.test(label)) return 0;
        if (/mail|邮箱|email/i.test(label)) return 1;
        if (/wechat|微信/i.test(label)) return 2;
        return 3;
      };
      return rank(a.label) - rank(b.label);
    });

  return (
    <ServicePageLayout
      title={t("pages.contact.title")}
      subtitle={t("pages.contact.subtitle")}
      ctaText={t("pages.contact.cta")}
      ctaLink="/contact"
      showBottomCta={false}
      showHero={false}
      showContentImage={false}
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 reveal-up">
          <p className="ttk-service-kicker text-xs mb-3">{isZh ? "快速联系" : "Quick Contact"}</p>
          <h2 className="ttk-service-title font-semibold mb-2">{isZh ? "立即建立连接" : "Start the Connection"}</h2>
          <p className="ttk-service-subtitle">
            {isZh ? "留下你的需求，我们会尽快联系你。" : "Share your request and we will reach out shortly."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map((item, i) => {
            const isWechatCard = /wechat|微信/i.test(item.label);
            const isEmailCard = /mail|邮箱|email/i.test(item.label);
            const isPhoneCard = /phone|电话/i.test(item.label);
            const Icon = /mail|邮箱|email/i.test(item.label) ? Mail : Phone;

            return (
              <div
                key={`${item.label}-${i}`}
                className="ttk-service-panel rounded-2xl p-6 md:p-8 min-h-[190px] flex items-center justify-center text-center reveal-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                {isWechatCard ? (
                  <img
                    src={wechatQr}
                    alt="WeChat QR Code"
                    width={224}
                    height={224}
                    className="w-28 h-28 rounded-md border border-border object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div>
                    <Icon className="w-8 h-8 text-foreground mx-auto mb-3" />
                    <div className="text-sm text-muted-foreground mb-1">{item.label}</div>
                    {item.value?.trim() ? (
                      <div className="font-semibold text-xl">
                        {isEmailCard ? (
                          <a href={`mailto:${item.value}`} className="hover:text-primary transition-colors">
                            {item.value}
                          </a>
                        ) : isPhoneCard ? (
                          <a href={`tel:${item.value.replace(/\D/g, "")}`} className="hover:text-primary transition-colors">
                            {item.value}
                          </a>
                        ) : (
                          item.value
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 reveal-up">
          <div className="ttk-service-panel rounded-xl p-4 text-sm text-muted-foreground">
            <span className="font-medium text-foreground mr-2">{isZh ? "响应时间" : "Response"}</span>
            {isZh ? "工作日通常 24 小时内回复" : "Usually within 24 hours on business days"}
          </div>
          <div className="ttk-service-panel rounded-xl p-4 text-sm text-muted-foreground">
            <span className="font-medium text-foreground mr-2">{isZh ? "服务时段" : "Hours"}</span>
            {isZh ? "周一至周六 10:00 - 19:00" : "Mon-Sat 10:00 - 19:00"}
          </div>
        </div>
      </div>
    </ServicePageLayout>
  );
};

export default Contact;
